// #5850 (CTO size-law round, 2026-08-31) — split out of governed-capability-manifest.ts to
// bring that touched file back under the 1,694-line ceiling. Pure vocabulary: the closed unit
// set a required input's value may be stated in, and the one compatibility rule every caller
// uses to authenticate a supplied value against a requirement's declared unit. Content-identical
// move — no logic change, every exported symbol identical, re-exported from
// governed-capability-manifest.ts so its own public API is unchanged.

function frozenTuple<const T extends readonly string[]>(values: T): Readonly<T> {
  return Object.freeze([...values]) as unknown as Readonly<T>;
}

export const EVIDENCE_STALE_BEHAVIORS = frozenTuple(['block', 'warn', 'allow_with_gap'] as const);

export const GOVERNED_CAPABILITY_OUTPUT_ENVELOPES = frozenTuple([
  'GovernedToolOutputEnvelope',
] as const);

export type GovernedCapabilityOutputEnvelope =
  (typeof GOVERNED_CAPABILITY_OUTPUT_ENVELOPES)[number];

export const CAPABILITY_REQUIREMENT_VALUE_TYPES = frozenTuple([
  'string',
  'number',
  'boolean',
] as const);

export type CapabilityRequirementValueType =
  (typeof CAPABILITY_REQUIREMENT_VALUE_TYPES)[number];

/**
 * #5042 — the governed unit the VALUE of a required input is stated in.
 *
 * `valueType` fixes the JS type and nothing else, so the bare number `8` cannot
 * tell a percent from a basis point from a dollar. Every consumer of a required
 * input applies unit semantics to it somewhere downstream, which makes the unit
 * part of the requirement's identity: it is declared beside the type, not
 * recovered later from the requirement's name, its label, or the words the
 * reader happened to use.
 *
 * The enum is CLOSED for the same reason every other planner vocabulary is
 * closed: an open unit string is a free-text field a model can fill with
 * anything, and a value authenticated under a unit the requirement never meant
 * is a fabricated number wearing a governed provenance badge.
 *
 * Omission is not "any unit". Omission means UNITLESS, and a unitless
 * requirement admits only a supply that states no unit at all.
 */
export const CAPABILITY_REQUIREMENT_UNITS = frozenTuple([
  'percent',
  'percentage_points',
  'bps',
  'usd',
  'months',
] as const);

export type CapabilityRequirementUnit =
  (typeof CAPABILITY_REQUIREMENT_UNITS)[number];

/**
 * The spellings a supplied unit may use for each governed unit. Compared after
 * trimming and case folding, and after nothing else.
 *
 * These are DECLARATIONS, not a synonym engine. Nothing here is derived at run
 * time from a requirement id, a label, an example or the reader's question, and
 * a near miss has no fallback: a spelling a reader uses that is not listed is
 * not resolved by inference — a requirement author adds it here, in review, or
 * the plan leaves that input unresolved.
 */
export const CAPABILITY_REQUIREMENT_UNIT_SPELLINGS: Readonly<
  Record<CapabilityRequirementUnit, readonly string[]>
> = Object.freeze({
  percent: frozenTuple(['%', 'pct', 'percent', 'percentage'] as const),
  percentage_points: frozenTuple([
    'pp',
    'ppt',
    'ppts',
    'percentage point',
    'percentage points',
  ] as const),
  bps: frozenTuple(['bp', 'bps', 'basis point', 'basis points'] as const),
  usd: frozenTuple(['$', 'usd', 'dollar', 'dollars'] as const),
  months: frozenTuple(['month', 'months'] as const),
});

/**
 * The one compatibility rule, shared by every caller that authenticates a
 * supplied value against a requirement. A governed unit admits only its own
 * declared spellings and never the absence of a unit; a unitless requirement
 * admits only the absence of a unit. No conversion, ever — an incompatible unit
 * is a rejection, never something to rescale.
 */
export function capabilityRequirementUnitAdmits(
  unit: CapabilityRequirementUnit | null | undefined,
  suppliedUnit: string | null | undefined
): boolean {
  const supplied =
    typeof suppliedUnit === 'string' ? suppliedUnit.trim().toLowerCase() : null;
  if (unit === null || unit === undefined) return supplied === null;
  const spellings = CAPABILITY_REQUIREMENT_UNIT_SPELLINGS[unit] as
    | readonly string[]
    | undefined;
  return supplied !== null && (spellings?.includes(supplied) ?? false);
}
